import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  TextField,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  MenuBook as DocsIcon,
} from '@mui/icons-material';
import { apiKeyApi } from '../../services/api';
import { useApp } from '../../context/AppContext';
import { LoadingSpinner, EmptyState } from '../common';
import { formatRelativeDate } from '../../utils/helpers';
import ApiKeyForm from './ApiKeyForm';
import ApiKeyDocs from './ApiKeyDocs';

const ApiKeyManager = () => {
  const { showSnackbar } = useApp();

  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form dialog
  const [formOpen, setFormOpen] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [generatedKey, setGeneratedKey] = useState(null);

  // Menu
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedKey, setSelectedKey] = useState(null);

  // Regenerate dialog
  const [regenerateDialog, setRegenerateDialog] = useState(false);
  const [regeneratedKey, setRegeneratedKey] = useState(null);

  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState(false);

  // Docs dialog
  const [docsOpen, setDocsOpen] = useState(false);

  const fetchApiKeys = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiKeyApi.getAll();
      setApiKeys(response.data.apiKeys);
    } catch (error) {
      showSnackbar('Failed to load API keys', 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchApiKeys();
  }, [fetchApiKeys]);

  const handleMenuOpen = (event, key) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedKey(key);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedKey(null);
  };

  const handleCreateClick = () => {
    setEditingKey(null);
    setGeneratedKey(null);
    setFormOpen(true);
  };

  const handleEditClick = () => {
    setEditingKey(selectedKey);
    setGeneratedKey(null);
    setFormOpen(true);
    handleMenuClose();
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setEditingKey(null);
    setGeneratedKey(null);
    fetchApiKeys();
  };

  const handleSave = async (data) => {
    try {
      if (editingKey) {
        await apiKeyApi.update(editingKey._id, data);
        showSnackbar('API key updated', 'success');
        handleFormClose();
      } else {
        const response = await apiKeyApi.create(data);
        setGeneratedKey(response.data.key);
        showSnackbar('API key created', 'success');
      }
      fetchApiKeys();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to save API key', 'error');
      throw error;
    }
  };

  const handleToggleActive = async () => {
    try {
      await apiKeyApi.update(selectedKey._id, { isActive: !selectedKey.isActive });
      showSnackbar(`API key ${selectedKey.isActive ? 'deactivated' : 'activated'}`, 'success');
      fetchApiKeys();
    } catch (error) {
      showSnackbar('Failed to update API key', 'error');
    }
    handleMenuClose();
  };

  const handleRegenerateClick = () => {
    setRegenerateDialog(true);
    handleMenuClose();
  };

  const handleRegenerateConfirm = async () => {
    try {
      const response = await apiKeyApi.regenerate(selectedKey._id);
      setRegeneratedKey(response.data.key);
      showSnackbar('API key regenerated', 'success');
      fetchApiKeys();
    } catch (error) {
      showSnackbar('Failed to regenerate API key', 'error');
    }
  };

  const handleRegenerateClose = () => {
    setRegenerateDialog(false);
    setRegeneratedKey(null);
    setSelectedKey(null);
  };

  const handleDeleteClick = () => {
    setDeleteDialog(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    try {
      await apiKeyApi.delete(selectedKey._id);
      showSnackbar('API key deleted', 'success');
      fetchApiKeys();
    } catch (error) {
      showSnackbar('Failed to delete API key', 'error');
    }
    setDeleteDialog(false);
    setSelectedKey(null);
  };

  const handleCopyPrefix = (prefix) => {
    navigator.clipboard.writeText(prefix);
    showSnackbar('Copied to clipboard', 'success');
  };

  if (loading) {
    return <LoadingSpinner message="Loading API keys..." />;
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              API Keys
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage API keys for external form submissions
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<DocsIcon />}
            onClick={() => setDocsOpen(true)}
            size="small"
            sx={{ mr: 1 }}
          >
            Docs
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateClick}
            size="small"
          >
            Create API Key
          </Button>
        </Box>

        {apiKeys.length === 0 ? (
          <EmptyState
            title="No API keys"
            description="Create an API key to allow external websites to submit contact forms"
            actionLabel="Create API Key"
            onAction={handleCreateClick}
          />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Key Prefix</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Origins</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Fields</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Usage</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {apiKeys.map((key) => (
                  <TableRow key={key._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {key.name}
                      </Typography>
                      {key.description && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {key.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.8rem' }}>
                          {key.keyPrefix}
                        </Typography>
                        <Tooltip title="Copy prefix">
                          <IconButton
                            size="small"
                            onClick={() => handleCopyPrefix(key.keyPrefix)}
                            sx={{ p: 0.25 }}
                          >
                            <CopyIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {key.allowedOrigins?.length > 0 ? (
                        <Typography variant="caption">
                          {key.allowedOrigins.length} origin{key.allowedOrigins.length !== 1 ? 's' : ''}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="warning.main">
                          All origins
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {key.fieldMappings?.length || 0} field{(key.fieldMappings?.length || 0) !== 1 ? 's' : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {key.usageCount || 0} requests
                      </Typography>
                      {key.lastUsedAt && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Last: {formatRelativeDate(key.lastUsedAt)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={key.isActive ? 'Active' : 'Inactive'}
                        color={key.isActive ? 'success' : 'default'}
                        size="small"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, key)}
                      >
                        <MoreIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditClick}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleToggleActive}>
          {selectedKey?.isActive ? 'Deactivate' : 'Activate'}
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleRegenerateClick}>
          <RefreshIcon fontSize="small" sx={{ mr: 1 }} />
          Regenerate Key
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* API Key Form Dialog */}
      <ApiKeyForm
        open={formOpen}
        onClose={handleFormClose}
        onSave={handleSave}
        apiKey={editingKey}
        generatedKey={generatedKey}
      />

      {/* Regenerate Confirmation Dialog */}
      <Dialog open={regenerateDialog} onClose={handleRegenerateClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {regeneratedKey ? 'New API Key Generated' : 'Regenerate API Key?'}
        </DialogTitle>
        <DialogContent>
          {regeneratedKey ? (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                Your new API key has been generated. Copy it now - you won't be able to see it again!
              </Alert>
              <TextField
                fullWidth
                label="New API Key"
                value={regeneratedKey}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => {
                          navigator.clipboard.writeText(regeneratedKey);
                          showSnackbar('Copied to clipboard', 'success');
                        }}
                        edge="end"
                      >
                        <CopyIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: { fontFamily: 'monospace' },
                }}
              />
            </Box>
          ) : (
            <Alert severity="warning">
              This will invalidate the current API key for "{selectedKey?.name}". Any applications using this key will need to be updated with the new key.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {regeneratedKey ? (
            <Button variant="contained" onClick={handleRegenerateClose}>
              Done
            </Button>
          ) : (
            <>
              <Button onClick={handleRegenerateClose}>Cancel</Button>
              <Button variant="contained" color="warning" onClick={handleRegenerateConfirm}>
                Regenerate
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete API Key?</DialogTitle>
        <DialogContent>
          <Alert severity="error">
            This will permanently delete the API key "{selectedKey?.name}". Any applications using this key will stop working.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* API Documentation Dialog */}
      <ApiKeyDocs open={docsOpen} onClose={() => setDocsOpen(false)} />
    </Card>
  );
};

export default ApiKeyManager;
